/**
 * CacheService provides a multi-layer caching system for frequently accessed data
 * Implements both in-memory and localStorage caching with TTL support
 */
import { logger } from '../utils/logger';

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in seconds
}

export interface CacheStats {
  memoryHits: number;
  memoryMisses: number;
  storageHits: number;
  storageMisses: number;
  totalItems: number;
  memorySize: number;
}

export class CacheService {
  private memoryCache = new Map<string, CacheItem<any>>();
  private stats: CacheStats = {
    memoryHits: 0,
    memoryMisses: 0,
    storageHits: 0,
    storageMisses: 0,
    totalItems: 0,
    memorySize: 0
  };
  
  // Maximum items in memory cache to prevent memory leaks
  private readonly MAX_MEMORY_ITEMS = 1000;
  private readonly STORAGE_PREFIX = 'aisg_cache_';

  /**
   * Get an item from cache (checks memory first, then localStorage)
   */
  async get<T>(key: string): Promise<T | null> {
    // Check memory cache first
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && this.isItemValid(memoryItem)) {
      this.stats.memoryHits++;
      return memoryItem.data as T;
    }

    if (memoryItem) {
      // Item expired, remove from memory
      this.memoryCache.delete(key);
    }
    this.stats.memoryMisses++;

    // Check localStorage
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const storageItem = localStorage.getItem(storageKey);
      
      if (storageItem) {
        const parsed: CacheItem<T> = JSON.parse(storageItem);
        
        if (this.isItemValid(parsed)) {
          this.stats.storageHits++;
          
          // Promote to memory cache if there's space
          if (this.memoryCache.size < this.MAX_MEMORY_ITEMS) {
            this.memoryCache.set(key, parsed);
          }
          
          return parsed.data;
        } else {
          // Item expired, remove from storage
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      logger.warn('Error reading from localStorage cache:', error);
    }

    this.stats.storageMisses++;
    return null;
  }

  /**
   * Set an item in cache (stores in both memory and localStorage)
   */
  async set<T>(key: string, data: T, ttlSeconds = 300): Promise<void> {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttlSeconds
    };

    // Store in memory cache
    if (this.memoryCache.size >= this.MAX_MEMORY_ITEMS) {
      // Remove oldest item to make space
      const oldestKey = this.memoryCache.keys().next().value;
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
      }
    }
    
    this.memoryCache.set(key, item);

    // Store in localStorage
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      localStorage.setItem(storageKey, JSON.stringify(item));
    } catch (error) {
      logger.warn('Error writing to localStorage cache:', error);
      // Continue without localStorage if it fails (e.g., quota exceeded)
    }

    this.updateStats();
  }

  /**
   * Delete an item from cache
   */
  async delete(key: string): Promise<void> {
    // Remove from memory
    this.memoryCache.delete(key);

    // Remove from localStorage
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      logger.warn('Error removing from localStorage cache:', error);
    }

    this.updateStats();
  }

  /**
   * Clear all cache items
   */
  async clear(): Promise<void> {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage cache items
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      logger.warn('Error clearing localStorage cache:', error);
    }

    this.resetStats();
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Get cache hit ratio
   */
  getHitRatio(): {
    memory: number;
    storage: number;
    overall: number;
  } {
    const memoryTotal = this.stats.memoryHits + this.stats.memoryMisses;
    const storageTotal = this.stats.storageHits + this.stats.storageMisses;
    const overallTotal = memoryTotal + storageTotal;

    return {
      memory: memoryTotal > 0 ? this.stats.memoryHits / memoryTotal : 0,
      storage: storageTotal > 0 ? this.stats.storageHits / storageTotal : 0,
      overall: overallTotal > 0 ? (this.stats.memoryHits + this.stats.storageHits) / overallTotal : 0
    };
  }

  /**
   * Clean up expired items from both memory and localStorage
   */
  async cleanup(): Promise<number> {
    let removedCount = 0;

    // Clean memory cache
    const memoryKeysToRemove: string[] = [];
    this.memoryCache.forEach((item, key) => {
      if (!this.isItemValid(item)) {
        memoryKeysToRemove.push(key);
      }
    });

    memoryKeysToRemove.forEach(key => {
      this.memoryCache.delete(key);
      removedCount++;
    });

    // Clean localStorage cache
    try {
      const storageKeysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          try {
            const item = JSON.parse(localStorage.getItem(key) || '');
            if (!this.isItemValid(item)) {
              storageKeysToRemove.push(key);
            }
          } catch (error) {
            // Invalid JSON, remove it
            storageKeysToRemove.push(key);
          }
        }
      }

      storageKeysToRemove.forEach(key => {
        localStorage.removeItem(key);
        removedCount++;
      });
    } catch (error) {
      logger.warn('Error during localStorage cleanup:', error);
    }

    this.updateStats();
    return removedCount;
  }

  /**
   * Get or set pattern - if key exists and is valid, return it; otherwise compute and cache
   */
  async getOrSet<T>(
    key: string, 
    computeFn: () => Promise<T>, 
    ttlSeconds = 300
  ): Promise<T> {
    const cached = await this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const computed = await computeFn();
    await this.set(key, computed, ttlSeconds);
    return computed;
  }

  /**
   * Check if multiple keys exist in cache
   */
  async hasKeys(keys: string[]): Promise<{ [key: string]: boolean }> {
    const result: { [key: string]: boolean } = {};
    
    for (const key of keys) {
      const item = await this.get(key);
      result[key] = item !== null;
    }
    
    return result;
  }

  /**
   * Get multiple items at once
   */
  async getMultiple<T>(keys: string[]): Promise<{ [key: string]: T | null }> {
    const result: { [key: string]: T | null } = {};
    
    for (const key of keys) {
      result[key] = await this.get<T>(key);
    }
    
    return result;
  }

  /**
   * Set multiple items at once
   */
  async setMultiple<T>(items: { [key: string]: T }, ttlSeconds = 300): Promise<void> {
    const promises = Object.entries(items).map(([key, value]) => 
      this.set(key, value, ttlSeconds)
    );
    
    await Promise.all(promises);
  }

  /**
   * Check if a cache item is still valid (not expired)
   */
  private isItemValid<T>(item: CacheItem<T>): boolean {
    const now = Date.now();
    const expirationTime = item.timestamp + (item.ttl * 1000);
    return now < expirationTime;
  }

  /**
   * Update cache statistics
   */
  private updateStats(): void {
    this.stats.totalItems = this.memoryCache.size;
    this.stats.memorySize = this.estimateMemorySize();
  }

  /**
   * Reset cache statistics
   */
  private resetStats(): void {
    this.stats = {
      memoryHits: 0,
      memoryMisses: 0,
      storageHits: 0,
      storageMisses: 0,
      totalItems: 0,
      memorySize: 0
    };
  }

  /**
   * Estimate memory usage of the cache (rough approximation)
   */
  private estimateMemorySize(): number {
    let size = 0;
    this.memoryCache.forEach((item, key) => {
      // Rough estimation: key size + JSON string size
      size += key.length * 2; // UTF-16 characters
      size += JSON.stringify(item).length * 2;
    });
    return size;
  }

  /**
   * Start automatic cleanup interval
   */
  startAutoCleanup(intervalMinutes = 30): () => void {
    const intervalMs = intervalMinutes * 60 * 1000;
    const intervalId = setInterval(() => {
      this.cleanup().then(removedCount => {
        if (removedCount > 0) {
          logger.debug(`Cache cleanup removed ${removedCount} expired items`);
        }
      });
    }, intervalMs);

    // Return cleanup function
    return () => clearInterval(intervalId);
  }

  /**
   * Create a namespaced cache service
   */
  namespace(prefix: string): CacheService {
    const namespacedService = Object.create(this);
    
    // Override methods to add prefix
    namespacedService.get = <T>(key: string) => this.get<T>(`${prefix}:${key}`);
    namespacedService.set = <T>(key: string, data: T, ttl?: number) => this.set(`${prefix}:${key}`, data, ttl);
    namespacedService.delete = (key: string) => this.delete(`${prefix}:${key}`);

    return namespacedService;
  }
}